import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import useElementWidth from "../../../hooks/useElementWidth";
import "./BubbleChart.scss";

const M = { top: 14, right: 18, bottom: 34, left: 46 };
const R_MIN = 5;
const R_MAX = 30;
const LABEL_H = 14;

const niceStep = (max, target = 4) => {
  const raw = max / target;
  const mag = 10 ** Math.floor(Math.log10(raw || 1));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
};

/**
 * BubbleChart — things placed by two measures at once, sized by a third. Built for
 * "volume against quality": which channels bring many people, and which bring people
 * who go on — the question a ranked list cannot answer, because it sorts by one.
 *
 * x is logarithmic by default (counts here span 1 to 10,000; on a linear axis every
 * source but the largest piles up against zero). Drawn in real pixels, measured from
 * the container, so labels stay legible at any width. Labels are nudged apart
 * vertically so neighbours never overprint.
 *
 * @param points     [{ key, label, x, y, size, color, sub?, faint? }] — `faint` for a point whose
 *                   y rests on too few to trust: drawn hollow, so it is seen but not believed
 * @param reference  optional { y, label } — a dashed line, e.g. the site-wide rate
 * @param yMax       optional ceiling for the y axis (1 for a rate, so nothing implies 150%)
 */
const BubbleChart = ({
  points, xLabel, yLabel, xFormat, yFormat, logX = true, height = 320, reference,
  onPointClick, activeKey, emptyText = "Nothing to plot in this period.", minX = 1, yMax,
}) => {
  const [ref, width] = useElementWidth(640);
  const [hover, setHover] = useState(null);
  const fx = xFormat || ((v) => Number(v).toLocaleString("en-GB"));
  const fy = yFormat || ((v) => `${Math.round(v * 100)}%`);

  const plot = useMemo(() => {
    const pts = (points || []).filter((p) => p.x >= minX && Number.isFinite(p.y));
    if (!pts.length || !width) return null;
    const w = width - M.left - M.right;
    const h = height - M.top - M.bottom;
    const xMax = Math.max(...pts.map((p) => p.x));
    const lo = logX ? Math.log10(minX) : 0;
    const hi = logX ? Math.ceil(Math.log10(xMax * 1.15)) : xMax * 1.08;
    const sx = (v) => M.left + ((logX ? Math.log10(Math.max(v, minX)) : v) - lo) / (hi - lo || 1) * w;
    const yTop = Math.max(...pts.map((p) => p.y), reference ? reference.y : 0) * 1.12 || 1;
    const yStep = niceStep(yTop);
    const yMaxNice = yMax != null ? Math.min(Math.ceil(yTop / yStep) * yStep, yMax) : Math.ceil(yTop / yStep) * yStep;
    const sy = (v) => M.top + h - (v / yMaxNice) * h;
    const sMax = Math.max(1, ...pts.map((p) => p.size || 0));
    const placed = pts
      .map((p) => ({ ...p, cx: sx(p.x), cy: sy(p.y), r: R_MIN + (R_MAX - R_MIN) * Math.sqrt((p.size || 0) / sMax) }))
      .sort((a, b) => b.r - a.r); // big ones behind
    // Labels: to the right of the bubble, nudged down until clear of those already placed.
    const taken = [];
    [...placed].sort((a, b) => a.cy - b.cy).forEach((p) => {
      let ly = p.cy;
      const lx = p.cx + p.r + 5;
      const lw = String(p.label).length * 6.6;
      const clash = (y) => taken.some((t) => Math.abs(t.y - y) < LABEL_H && lx < t.x + t.w && lx + lw > t.x);
      let guard = 0;
      while (clash(ly) && guard < 12) { ly += LABEL_H / 2; guard += 1; }
      taken.push({ x: lx, y: ly, w: lw });
      p.lx = Math.min(lx, width - M.right - lw);
      p.ly = Math.min(ly, M.top + h);
      p.flip = lx + lw > width - M.right; // no room on the right: label on the left
      if (p.flip) p.lx = p.cx - p.r - 5;
    });
    const xTicks = [];
    if (logX) for (let e = Math.ceil(lo); e <= hi; e += 1) xTicks.push(10 ** e);
    else for (let v = 0; v <= hi; v += niceStep(hi)) xTicks.push(v);
    const yTicks = [];
    for (let v = 0; v <= yMaxNice + 1e-9; v += yStep) yTicks.push(v);
    return { placed, xTicks, yTicks, sx, sy, w, h };
  }, [points, width, height, logX, minX, reference, yMax]);

  return (
    <div className="bubble-chart" ref={ref}>
      {!plot ? <p className="bubble-chart__empty">{emptyText}</p> : (
        <svg width={width} height={height} role="img" aria-label={`${yLabel} against ${xLabel}`}>
          {plot.yTicks.map((v) => (
            <g key={`y${v}`}>
              <line x1={M.left} x2={M.left + plot.w} y1={plot.sy(v)} y2={plot.sy(v)} className="bubble-chart__grid" />
              <text x={M.left - 8} y={plot.sy(v)} className="bubble-chart__tick" textAnchor="end" dominantBaseline="middle">{fy(v)}</text>
            </g>
          ))}
          {plot.xTicks.map((v) => (
            <g key={`x${v}`}>
              <line x1={plot.sx(v)} x2={plot.sx(v)} y1={M.top} y2={M.top + plot.h} className="bubble-chart__grid bubble-chart__grid--x" />
              <text x={plot.sx(v)} y={M.top + plot.h + 16} className="bubble-chart__tick" textAnchor="middle">{fx(v)}</text>
            </g>
          ))}
          <text x={M.left + plot.w} y={height - 4} className="bubble-chart__axis" textAnchor="end">{xLabel}{logX ? " (each step ×10)" : ""} →</text>
          <text x={M.left - 38} y={M.top + 2} className="bubble-chart__axis" dominantBaseline="hanging" transform={`rotate(-90 ${M.left - 38} ${M.top + 2})`} textAnchor="end">{yLabel} →</text>
          {reference && Number.isFinite(reference.y) && (
            <g>
              <line x1={M.left} x2={M.left + plot.w} y1={plot.sy(reference.y)} y2={plot.sy(reference.y)} className="bubble-chart__ref" />
              <text x={M.left + 6} y={plot.sy(reference.y) - 5} textAnchor="start" className="bubble-chart__ref-label">{reference.label}</text>
            </g>
          )}
          {plot.placed.map((p) => {
            const dim = (hover && hover !== p.key) || (activeKey && activeKey !== p.key);
            return (
              <g
                key={p.key}
                className={`bubble-chart__point${onPointClick ? " bubble-chart__point--clickable" : ""}`}
                style={{ opacity: dim ? 0.3 : 1 }}
                onMouseEnter={() => setHover(p.key)}
                onMouseLeave={() => setHover(null)}
                onClick={onPointClick ? () => onPointClick(p) : undefined}
              >
                <circle cx={p.cx} cy={p.cy} r={p.r} fill={p.color || "#0e7490"} stroke={p.faint ? p.color || "#0e7490" : undefined}
                  className={`bubble-chart__bubble${p.faint ? " bubble-chart__bubble--faint" : ""}`} />
                <title>{`${p.label}: ${fx(p.x)} ${xLabel.toLowerCase()} · ${fy(p.y)} ${yLabel.toLowerCase()}${p.sub ? ` · ${p.sub}` : ""}`}</title>
              </g>
            );
          })}
          {plot.placed.map((p) => (
            <text key={`l${p.key}`} x={p.lx} y={p.ly} dominantBaseline="middle" textAnchor={p.flip ? "end" : "start"}
              className="bubble-chart__label" style={{ opacity: (hover && hover !== p.key) || (activeKey && activeKey !== p.key) ? 0.3 : 1 }}>
              {p.label}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
};

BubbleChart.propTypes = {
  points: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.string.isRequired, x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired, size: PropTypes.number, color: PropTypes.string, sub: PropTypes.string,
  })).isRequired,
  xLabel: PropTypes.string.isRequired,
  yLabel: PropTypes.string.isRequired,
  xFormat: PropTypes.func,
  yFormat: PropTypes.func,
  logX: PropTypes.bool,
  height: PropTypes.number,
  reference: PropTypes.shape({ y: PropTypes.number, label: PropTypes.string }),
  onPointClick: PropTypes.func,
  activeKey: PropTypes.string,
  emptyText: PropTypes.string,
  minX: PropTypes.number,
  yMax: PropTypes.number,
};

export default BubbleChart;
